import { first, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { assetQuery, buttonQuery, matrixBlockQuery } from '../../content/query-snippets';
import { cmsImagesPropTypes } from '../../prop-types';
import { Box, Container, Flex } from '../grid';
import { buttonTransform, imageTransform } from '../../content/transforms';
import { Video } from '../video';
import { BlockHeader } from '../block-header';
import { Button } from '../button/new';
import { Image } from '../image';

const VideoContentBlock = ({ heading, viBody, video, image, button }) => {
  const hasVideo = !isEmpty(video);
  const videoData = hasVideo ? first(video) : null;
  const transformedImage = imageTransform(image);
  const transformedButtonProps = buttonTransform(button);

  return (
    <Container
      variant="featuredContent"
      mx="96px"
      my={{ xs: '64px', lg: '120px' }}
      width="100%"
      backgroundColor="transparent"
    >
      <BlockHeader
        heading={heading}
        body={viBody}
        mb={!isEmpty(transformedButtonProps) ? { xs: '20px', lg: '20px' } : { xs: '36px', lg: '56px' }}
      />

      {!isEmpty(transformedButtonProps) && (
        <Flex width="100%" justifyContent="center" mb={{ xs: '36px', lg: '56px' }}>
          <Button
            size="tall"
            variant="secondary"
            onClick={transformedButtonProps?.onClick}
            href={transformedButtonProps?.href}
            target={transformedButtonProps?.target}
            widthType="fit"
          >
            {transformedButtonProps.text}
          </Button>
        </Flex>
      )}

      <Flex width="100%" justifyContent="center">
        <Box
          width={{ xs: '100%', lg: '948px' }}
          aspectRatio="16 / 9"
          borderRadius={{ xs: '24px', md: '32px' }}
          overflow="hidden"
        >
          {hasVideo ? (
            <Video
              autoPlay={videoData.autoPlay}
              mimeType={videoData.isIframeVideo ? 'text/html' : 'video/mp4'}
              poster={videoData.poster ? imageTransform(videoData.poster) : null}
              height="100%"
              width="100%"
              src={videoData.source}
              title={videoData.title}
            />
          ) : (
            transformedImage && (
              <Image
                alt={transformedImage?.alt}
                height="100%"
                focalPoint={transformedImage?.focalPoint}
                objectFit={transformedImage?.objectFit || 'cover'}
                src={transformedImage?.url}
              />
            )
          )}
        </Box>
      </Flex>
    </Container>
  );
};

VideoContentBlock.propTypes = {
  viBody: PropTypes.string,
  heading: PropTypes.string,
  image: cmsImagesPropTypes,
  button: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        href: PropTypes.string,
        target: PropTypes.string,
      })
    ),
  ]),
  video: PropTypes.arrayOf(
    PropTypes.shape({
      autoPlay: PropTypes.bool,
      isIframeVideo: PropTypes.bool,
      poster: cmsImagesPropTypes,
      source: PropTypes.string,
      title: PropTypes.string,
    })
  ),
};

VideoContentBlock.defaultProps = {
  viBody: null,
  heading: null,
  image: null,
  button: null,
  video: null,
};

VideoContentBlock.query = `
	... on contentMatrix_videoContentBlock_BlockType {
		${matrixBlockQuery}
		viBody
		heading
    ${assetQuery('image')}
    ${buttonQuery}
		video {
			... on videos_Entry {
				${assetQuery('poster')}
				title
        autoPlay
        isIframeVideo
        source
			}
		}
	}
`;

export { VideoContentBlock };
